(function () {
  class MiniCartDrawer {
    constructor() {
      this.container = null;
      this.panel = null;
      this.state = 'closed';
      this.animationTimer = null;
      this.drag = null;
      this.suppressClick = false;
      this.unloaded = false;
      this.panelCleanups = [];
      this.boundDocumentClick = event => this._handleDocumentClick(event);
      this.boundKeyDown = event => this._handleKeyDown(event);
      this.boundResize = () => this._handleResize();
      this._bindDocument();
      this.refresh();
    }

    _bindDocument() {
      document.addEventListener('click', this.boundDocumentClick);
      document.addEventListener('keydown', this.boundKeyDown);
      window.addEventListener('resize', this.boundResize);
    }

    refresh() {
      this._removeLegacyCartBindings();
      this._ensureMobileCartMarkup();
      document.querySelectorAll('.cart-container .cart_content').forEach(panel => this._preparePanel(panel));
    }

    _removeLegacyCartBindings() {
      if (!window.jQuery) return;
      window.jQuery('.mini_cart').off('click');
      window.jQuery('.cart-container').off('mouseenter mouseleave');
      window.jQuery('.cart_content__continue-shopping').off('click');
    }

    _ensureMobileCartMarkup() {
      const mobileContainer = document.querySelector('#header .cart-container');
      if (!mobileContainer || mobileContainer.querySelector('.cart_content')) return;
      const template = document.querySelector('.header .cart_content');
      if (template) {
        const clone = template.cloneNode(true);
        clone.querySelectorAll('[id]').forEach(element => {
          element.id = `${element.id}-mobile`;
        });
        clone.querySelectorAll('[for]').forEach(element => {
          element.htmlFor = `${element.htmlFor}-mobile`;
        });
        mobileContainer.appendChild(clone);
      }
    }

    _preparePanel(panel) {
      if (panel.dataset.miniCartPrepared === 'true') return;
      panel.dataset.miniCartPrepared = 'true';
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', 'Shopping cart');
      panel.dataset.miniCartState = 'closed';
      const handlers = {
        pointerdown: event => this._startDrag(event, panel),
        pointermove: event => this._moveDrag(event, panel),
        pointerup: event => this._finishDrag(event, panel),
        pointercancel: event => this._cancelDrag(event, panel),
      };
      Object.entries(handlers).forEach(([eventName, handler]) => panel.addEventListener(eventName, handler));
      this.panelCleanups.push(() => {
        Object.entries(handlers).forEach(([eventName, handler]) => panel.removeEventListener(eventName, handler));
        delete panel.dataset.miniCartPrepared;
      });
    }

    _handleDocumentClick(event) {
      if (this.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
        return;
      }

      const trigger = event.target.closest('.mini_cart');
      if (trigger) {
        if (window.PXUTheme && window.PXUTheme.theme_settings && window.PXUTheme.theme_settings.cart_action === 'redirect_cart') return;
        const container = trigger.closest('.cart-container');
        if (!container || !this._hasPanel(container)) return;
        event.preventDefault();
        event.stopPropagation();
        this.toggle(container);
        return;
      }

      if (!this.container || !this.panel) return;
      if (event.target.closest('.cart_content .closeButton, .cart_content__continue-shopping')) {
        this.close();
        return;
      }

      if (!event.target.closest('.cart_content')) this.close();
    }

    _handleKeyDown(event) {
      if (event.key === 'Escape' && this.state !== 'closed') {
        event.preventDefault();
        this.close();
      }
    }

    _hasPanel(container) {
      return Boolean(container.querySelector('.cart_content'));
    }

    toggle(container) {
      if (this.container === container && this.state !== 'closed') {
        this.close();
      } else {
        this.open(container);
      }
    }

    open(container) {
      const panel = container && container.querySelector('.cart_content');
      if (!panel) return;

      if (window.mobileMenuDrawer) window.mobileMenuDrawer.close();
      if (this.container && this.container !== container) this._closeCurrent(false);
      document.body.classList.remove('cart-closing');
      this._preparePanel(panel);
      window.clearTimeout(this.animationTimer);
      this.container = container;
      this.panel = panel;
      this._setOffset('100%');
      document.querySelectorAll('.cart-container.active_link').forEach(element => element.classList.remove('active_link'));
      container.classList.add('active_link');
      document.body.classList.add('openCart');
      this._setState('opening');
      this._syncScrollLock();
      // Force the off-canvas position to commit before beginning the legacy
      // right-property transition. Timers continue to run in throttled tabs.
      void panel.offsetWidth;
      this._queueMotion(() => {
        if (this.state !== 'opening') return;
        this._setOffset('0px');
        this._setState('open');
      });
    }

    openActive() {
      const containers = [...document.querySelectorAll('.cart-container')];
      const container = containers
        .find(element => element.classList.contains('active_link') && this._hasPanel(element))
        || containers.find(element => this._hasPanel(element) && element.getClientRects().length > 0)
        || document.querySelector('#header .cart-container')
        || document.querySelector('.header .cart-container');
      if (container) this.open(container);
    }

    close() {
      this._closeCurrent(true);
    }

    _closeCurrent(animate) {
      if (!this.container || !this.panel || this.state === 'closed') {
        this._clearBodyState();
        document.body.classList.remove('cart-closing');
        return;
      }

      window.clearTimeout(this.animationTimer);
      this._setState(animate ? 'closing' : 'closed');
      this._clearBodyState();
      if (!animate) {
        document.body.classList.remove('cart-closing');
        this._setOffset('100%');
        this._clearContainer();
        return;
      }

      document.body.classList.add('cart-closing');
      void this.panel.offsetWidth;
      this._queueMotion(() => this._setOffset('100%'));
      this.animationTimer = window.setTimeout(() => {
        this._clearContainer();
      }, 200);
    }

    _clearContainer() {
      this._clearBodyState();
      document.body.classList.remove('cart-closing');
      if (!this.panel || !this.container) return;
      this._setState('closed');
      this._setOffset('100%');
      this.container.classList.remove('active_link');
      this.container = null;
      this.panel = null;
    }

    _clearBodyState() {
      document.body.classList.remove('openCart', 'mobile-drawer-open', 'blocked-scroll');
    }

    _syncScrollLock() {
      const shouldLock = Boolean(
        this.container
        && this.panel
        && ['opening', 'open', 'dragging'].includes(this.state)
        && window.matchMedia('(max-width: 798px)').matches
      );
      document.body.classList.toggle('blocked-scroll', shouldLock);
    }

    _handleResize() {
      this._syncScrollLock();
      if (this.container && this.container.getClientRects().length === 0) {
        this._closeCurrent(false);
      }
    }

    _setState(state) {
      this.state = state;
      if (!this.panel) return;
      this.panel.dataset.miniCartState = state;
      this.panel.setAttribute('aria-hidden', state === 'closed' ? 'true' : 'false');
    }

    _setOffset(offset) {
      if (this.panel) this.panel.style.setProperty('--mini-cart-offset', offset);
    }

    _queueMotion(callback) {
      window.setTimeout(callback, 0);
    }

    _startDrag(event, panel) {
      if (this.panel !== panel || this.state !== 'open' || (event.pointerType === 'mouse' && event.button !== 0) || this._isInteractiveTarget(event.target)) return;
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: Date.now(),
        axis: null,
        moved: false,
      };
    }

    _isInteractiveTarget(target) {
      return Boolean(target && target.closest && target.closest('a, button, input, textarea, select, label, [contenteditable="true"], .js-change-quantity, [data-minicart-checkout-button]'));
    }

    _moveDrag(event, panel) {
      if (!this.drag || this.panel !== panel || event.pointerId !== this.drag.pointerId) return;
      const deltaX = event.clientX - this.drag.startX;
      const deltaY = event.clientY - this.drag.startY;
      if (!this.drag.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8) {
        this.drag.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        if (this.drag.axis === 'y') {
          this._releasePointer(event, panel);
          return;
        }
        panel.setPointerCapture(event.pointerId);
        this._setState('dragging');
      }
      if (this.drag.axis !== 'x') return;
      this.drag.moved = true;
      event.preventDefault();
      this._setOffset(`${Math.max(0, deltaX)}px`);
    }

    _finishDrag(event, panel) {
      if (!this.drag || this.panel !== panel || event.pointerId !== this.drag.pointerId) return;
      const delta = Math.max(0, event.clientX - this.drag.startX);
      const elapsed = Math.max(1, Date.now() - this.drag.startTime);
      const velocity = delta / elapsed;
      const shouldClose = this.drag.axis === 'x'
        && (delta > panel.offsetWidth * 0.28 || velocity > 0.65);
      this.suppressClick = this.drag.moved;
      this._releasePointer(event, panel);
      if (shouldClose) this.close();
      else {
        this._setState('open');
        this._queueMotion(() => this._setOffset('0px'));
      }
    }

    _cancelDrag(event, panel) {
      if (!this.drag || this.panel !== panel || event.pointerId !== this.drag.pointerId) return;
      this._releasePointer(event, panel);
      this._setState('open');
      this._setOffset('0px');
    }

    _releasePointer(event, panel) {
      if (panel.hasPointerCapture(event.pointerId)) panel.releasePointerCapture(event.pointerId);
      this.drag = null;
    }

    unload() {
      this.close();
      this.panelCleanups.splice(0).forEach(cleanup => cleanup());
      document.removeEventListener('click', this.boundDocumentClick);
      document.removeEventListener('keydown', this.boundKeyDown);
      window.removeEventListener('resize', this.boundResize);
      this.unloaded = true;
    }
  }

  window.MiniCartDrawer = MiniCartDrawer;
  window.initializeMiniCartDrawer = () => {
    const currentRoot = document.querySelector('.cart-container .cart_content');
    if (!currentRoot) return;
    if (window.miniCartDrawer && !window.miniCartDrawer.unloaded && window.miniCartDrawer.panel && window.miniCartDrawer.panel.isConnected) {
      window.miniCartDrawer.refresh();
      return;
    }
    if (window.miniCartDrawer) window.miniCartDrawer.unload();
    window.miniCartDrawer = new MiniCartDrawer();
  };

  document.addEventListener('DOMContentLoaded', () => window.initializeMiniCartDrawer());
}());
